import { BaseListViewStore, DateTimeCellData, HyperlinkCellData } from 'core/stores';
import { TableFilterTemplate, TableFilterDateTemplate, TableFilterNumericTemplate } from 'themes/components';
import { isSuperAdminOrganization, isWarehouse, notificationError, TableFilterUtility } from 'core/utils';
import { ConfirmModalParams } from 'core/models';
import { observable, action, makeObservable } from 'mobx';
import { BaasicTableSettingsViewStore } from 'common/stores';
import { ReceivingStore } from 'modules/receiving/stores';
import { checkPermissions } from 'core/stores';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, receivingStatus } from 'common/constants';
import { localStorageHandler } from 'core/services';
import { checkForColumnFilter, checkForColumnWidth, RemoveFiltersModalUtility } from 'core/utils';
import { ApplicationStore } from 'platform/application/stores';
import { ApplicationService } from 'common/data';
import _ from 'lodash';

class ReceivingListViewStore extends BaseListViewStore {
	@observable change = false;
	selectedItem = null;
	showActions = true;
	localStorageWidthKey = 'receivings-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ReceivingStore(rootStore),
			queryParams: {
				embed: ['receivingStatus', 'receivingType', 'warehouseTo', 'warehouseFrom', 'receivingProducts', 'seller'],
				filter: localStorageHandler.get('receiving-filter'),
			},
			tableData: {
				columns: [
					{
						key: 'ref',
						title: 'RECEIVINGVIEW.REF',
						dataCell: HyperlinkCellData,
						onClick: dataItem => this.routes.detail(dataItem.id),
						width: 200,
						selected: true,
						filter: null,
						locked: true,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'receivingStatus.name',
						title: 'RECEIVINGVIEW.STATUS',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						selected: false,
						sortable: true,
						lookupConstant: lookupRouteName.receivingStatus,
					},
					{
						key: 'seller.applicationTitle',
						title: 'RECEIVINGVIEW.SELLER',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						useAlias: true,
						selected: false,
						sortable: true,
						sellerOnly: true,
						dontSplit: true,
						enabled: isWarehouse(rootStore) || isSuperAdminOrganization(rootStore),
						service: 'applicationService',
					},
					{
						key: 'receivingType.name',
						title: 'RECEIVINGVIEW.TYPE',
						store: 'receivingListViewStore',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						selected: false,
						frozen: false,
						sortable: true,
						type: 'lookup',
						lookupConstant: lookupRouteName.receivingType,
					},
					{
						key: 'estimatedReceivingDate',
						title: 'RECEIVINGVIEW.ESTIMATEDRECEIVINGDATE',
						dataCell: DateTimeCellData,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'dateCreated',
						title: 'RECEIVINGVIEW.CREATEDDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'LLL',
						useLocalTime: true,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'actualReceivingDate',
						title: 'RECEIVINGVIEW.ACTUALRECEIVINGDATE',
						dataCell: DateTimeCellData,
						useLocalTime: true,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'warehouseTo.applicationTitle',
						title: 'RECEIVINGVIEW.WAREHOUSETO',
						service: 'applicationService',
						isWarehouseOnlyColumn: true,
						useAlias: true,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						embed: ['warehouseTo'],
					},
					{
						key: 'warehouseFrom.applicationTitle',
						title: 'RECEIVINGVIEW.WAREHOUSEFROM',
						service: 'applicationService',
						useAlias: true,
						isWarehouseOnlyColumn: true,
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						embed: ['warehouseFrom'],
					},
					{
						key: 'cost',
						title: 'RECEIVINGVIEW.COST',
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
				],
				sortObject: [
					{
						field: 'ref',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: item => this.routes.edit(item.id),
					onDelete: item =>
						(item.receivingStatus.abrv == receivingStatus.arrivedAtPort ||
							item.receivingStatus.abrv == receivingStatus.inTransit) &&
						item.receivingProducts.some(x => x.isReceived === true)
							? this.rootStore.notificationStore.error('LOOKUPDELETEERROR.RECEIVINGPORT')
							: this.deleteConfirmModalParams.open(
									{
										message: ['LOOKUPDELETECONFIRM.RECEIVING', { name: item.ref }],
									},
									item
							  ),
				},
			},
			permissions: {
				update: () => this.authStore.hasPermission('receivingSection.update'),
				create: () => this.authStore.hasPermission('receivingSection.create'),
				remove: () => this.authStore.hasPermission('receivingSection.remove'),
				read: () => this.authStore.hasPermission('receivingSection.read'),
				full: () => this.authStore.hasPermission('receivingSection.full'),
			},
			routes: {
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.receiving.list.edit', {
						id,
					}),
				detail: id =>
					this.rootStore.routerStore.goTo('master.app.main.receiving.list.detail', {
						id,
					}),
				create: () => this.rootStore.routerStore.goTo('master.app.main.receiving.list.add', {}),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(rootStore) || isWarehouse(rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findContainers);
		checkForColumnFilter(localStorageHandler.get('receiving-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'receiving-table-data';
		this.showActions = checkPermissions(this).actionsAllowed;
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.lookupStore = new LookupStore(rootStore);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.tableFilter = new TableFilterUtility({
			lookupStore: this.lookupStore,
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [this.listStore.service, new ApplicationService(this.rootStore.app.baasic.apiClient)],
		});
		this.setButtons();
		this.setHeaderModes();
		this.setSearchVisible();
	}

	@action.bound setAsChanged() {
		this.change = !this.change;
	}

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'receivingSection.create',
					text: 'RECEIVINGVIEW.CREATE',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
			],
		});
	}

	@action.bound
	deleteReceiving = async () => {
		this.suspend();
		const data = this.deleteConfirmModalParams.data;
		try {
			await this.listStore.deleteReceiving(data);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.RECEIVING', { name: data.ref }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			if (err.data != null) {
				this.rootStore.notificationStore.error([err.data, { name: data.ref }]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	@action.bound
	findContainers = async () => {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'ReceivingTableSettings',
			this.setAsChanged,
			this.findContainers
		);
		this.queryUtility.setFilterParams(this, null, this.tableFilter);
		await this.listStore.findReceiving(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		_.forEach(this.listStore.response.item, item => {
			if (item.warehouseFromId === null && item.warehouseFromName) {
				item.warehouseFrom = {
					applicationTitle: item.warehouseFromName,
				};
			}
			if (item.warehouseToId === null && item.warehouseToName) {
				item.warehouseTo = {
					applicationTitle: item.warehouseToName,
				};
			}
		});
		this.queryUtility.setFilterPersistance(this, 'receiving-filter', 'receiving-table-data');
		this.setButtons();
		this.resume();
	};

	deleteConfirmModalParams = new ConfirmModalParams({
		onConfirm: async () => {
			await this.deleteReceiving();
		},
	});
}

export default ReceivingListViewStore;



// WEBPACK FOOTER //
// ./src/modules/receiving/stores/ReceivingListViewStore.js